import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import {
  FaInfoCircle,
  FaMapMarkedAlt,
  FaRegCalendarAlt,
  FaUsers,
} from "react-icons/fa";
import { withTranslation } from "react-i18next";

import myConfig from "../../configs/config";
import { visibleMachinesForWeb } from "../Machine/machineVisibility";
import { getTracePeriodForHours } from "./dashboardTraceUtils";
import {
  activeTrailChip,
  calendarDayRange,
  catalogMachines,
  catalogPeople,
  dateInputToRange,
  machinesInRoster,
  nextVisibleTrailIds,
  peopleInRoster,
  peopleTracePeriodError,
  recentTrailDays,
  trailDayKey,
} from "./dashboardTrailPeople";
import "./DashboardTrailPeopleKpi.css";

function isoDate(date) {
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${date.getFullYear()}-${month}-${day}`;
}

function defaultRange() {
  const end = new Date();
  const start = new Date();
  start.setDate(end.getDate() - 6);
  return { start: isoDate(start), end: isoDate(end) };
}

function asList(payload) {
  if (Array.isArray(payload)) return payload;
  if (payload && Array.isArray(payload.results)) return payload.results;
  return [];
}

function TrailColumn({ title, allLabel, rows, checkedIds, onToggle, swatchFor }) {
  const allIds = rows.map((row) => String(row.id));
  const checked = Array.isArray(checkedIds) ? checkedIds.map(String) : allIds;
  const allOn = allIds.length > 0 && allIds.every((id) => checked.includes(id));

  return (
    <div className="dashboard-trail-people__column">
      <span className="dashboard-trail-people__column-title">{title}</span>
      <ul className="dashboard-trail-people__list">
        <li>
          <label>
            <input type="checkbox" checked={allOn} onChange={() => onToggle("*")} />
            <span>{allLabel}</span>
          </label>
        </li>
        {rows.map((row) => {
          const id = String(row.id);
          const color = swatchFor ? swatchFor(id) : "";
          return (
            <li key={id}>
              <label>
                <input
                  type="checkbox"
                  checked={checked.includes(id)}
                  onChange={() => onToggle(id)}
                />
                {color ? (
                  <span
                    className="dashboard-trail-people__swatch"
                    style={{ background: color }}
                  />
                ) : (
                  <span className="dashboard-trail-people__swatch is-machine" />
                )}
                <span className="dashboard-trail-people__name">{row.name}</span>
              </label>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function DashboardTrailPeopleKpi({
  t,
  farmId,
  operators,
  equipment,
  visibleIds,
  visibleMachineIdents,
  roster,
  operatorTraceColors,
  selected,
  noticeOpen,
  landplotPickMode = false,
  onToggleLandplotPickMode,
  onSelect,
  onCloseNotice,
  onApplyPeriod,
  onChange,
  onChangeMachines,
  onChangeRoster,
  tracePeriodStart,
  tracePeriodEnd,
  trailDayKeys,
  onToggleDay,
  onToggleHour,
}) {
  const [catalogOperators, setCatalogOperators] = useState([]);
  const [catalogEquipment, setCatalogEquipment] = useState([]);
  const [groupOpen, setGroupOpen] = useState(false);
  const [groupOperators, setGroupOperators] = useState(null);
  const [groupMachines, setGroupMachines] = useState(null);
  const initialRange = defaultRange();
  const [startValue, setStartValue] = useState(initialRange.start);
  const [endValue, setEndValue] = useState(initialRange.end);
  const [periodError, setPeriodError] = useState("");
  const [expanded, setExpanded] = useState(false);
  const [slowTipOpen, setSlowTipOpen] = useState(false);
  const [groupTipOpen, setGroupTipOpen] = useState(false);
  const [pickTipOpen, setPickTipOpen] = useState(false);
  const panelRef = useRef(null);
  const groupTipTimer = useRef(null);
  const pickTipTimer = useRef(null);

  const openQuickTip = (kind) => {
    window.clearTimeout(groupTipTimer.current);
    window.clearTimeout(pickTipTimer.current);
    if (kind === "group") {
      setPickTipOpen(false);
      groupTipTimer.current = window.setTimeout(() => setGroupTipOpen(true), 80);
    } else if (kind === "pick") {
      setGroupTipOpen(false);
      pickTipTimer.current = window.setTimeout(() => setPickTipOpen(true), 80);
    }
  };

  const closeQuickTip = (kind) => {
    if (kind === "group") {
      window.clearTimeout(groupTipTimer.current);
      setGroupTipOpen(false);
    } else if (kind === "pick") {
      window.clearTimeout(pickTipTimer.current);
      setPickTipOpen(false);
    }
  };

  useEffect(
    () => () => {
      window.clearTimeout(groupTipTimer.current);
      window.clearTimeout(pickTipTimer.current);
    },
    []
  );

  useEffect(() => {
    if (!farmId) return undefined;
    let cancelled = false;
    const headers = {
      Authorization: `Bearer ${localStorage.getItem("client-token")}`,
    };
    Promise.all([
      axios.get(`${myConfig.API_URL}/operator/?farm_id=${farmId}`, { headers }),
      axios.get(`${myConfig.API_URL}/equipment/?farm_id=${farmId}`, { headers }),
    ])
      .then(([operatorResponse, equipmentResponse]) => {
        if (cancelled) return;
        setCatalogOperators(asList(operatorResponse.data));
        setCatalogEquipment(visibleMachinesForWeb(asList(equipmentResponse.data)));
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [farmId]);

  useEffect(() => {
    if (!expanded) return undefined;
    const onPointerDown = (event) => {
      if (event.target.closest(".dashboard-trail-people-modal")) return;
      if (panelRef.current && panelRef.current.contains(event.target)) return;
      setExpanded(false);
    };
    document.addEventListener("mousedown", onPointerDown);
    return () => document.removeEventListener("mousedown", onPointerDown);
  }, [expanded]);

  useEffect(() => {
    if (!slowTipOpen) return undefined;
    const onPointerDown = (event) => {
      if (event.target.closest(".dashboard-trail-people__info, .dashboard-trail-people__slow-tip")) {
        return;
      }
      setSlowTipOpen(false);
    };
    document.addEventListener("mousedown", onPointerDown);
    return () => document.removeEventListener("mousedown", onPointerDown);
  }, [slowTipOpen]);

  const people = peopleInRoster(
    catalogPeople(catalogOperators, operators),
    roster && roster.operatorIds
  );
  const machines = machinesInRoster(
    catalogMachines(catalogEquipment, equipment),
    roster && roster.machineIdents
  );
  const peopleIds = people.map((person) => String(person.id));
  const machineIds = machines.map((machine) => String(machine.ident));
  const checkedPeople = Array.isArray(visibleIds) ? visibleIds.map(String) : peopleIds;
  const checkedMachines = Array.isArray(visibleMachineIdents)
    ? visibleMachineIdents.map(String)
    : machineIds;
  const shownCount =
    checkedPeople.filter((id) => peopleIds.includes(id)).length +
    checkedMachines.filter((id) =>
      machineIds.some((ident) => ident.toLowerCase() === id.toLowerCase())
    ).length;

  const emitPeople = (toggledId) => {
    if (typeof onChange !== "function") return;
    onChange(nextVisibleTrailIds(peopleIds, visibleIds, toggledId));
  };

  const emitMachines = (toggledId) => {
    if (typeof onChangeMachines !== "function") return;
    onChangeMachines(nextVisibleTrailIds(machineIds, visibleMachineIdents, toggledId));
  };

  const openGroup = () => {
    setGroupOperators(roster && roster.operatorIds);
    setGroupMachines(roster && roster.machineIdents);
    setGroupOpen(true);
  };

  const saveGroup = () => {
    if (typeof onChangeRoster === "function") {
      onChangeRoster({
        operatorIds: groupOperators,
        machineIdents: groupMachines,
      });
    }
    setGroupOpen(false);
  };

  const applyRange = (start, end) => {
    if (typeof onApplyPeriod === "function") onApplyPeriod(start, end);
  };

  const applyHours = (hours) => {
    if (typeof onToggleHour === "function") {
      onToggleHour(hours);
      return;
    }
    const period = getTracePeriodForHours(hours);
    applyRange(period.start, period.end);
  };

  const confirmPeriod = () => {
    const range = dateInputToRange(startValue, endValue);
    const error = range ? peopleTracePeriodError(range.start, range.end) : "Informe o início e o fim.";
    if (error) {
      setPeriodError(error);
      return;
    }
    setPeriodError("");
    if (typeof onApplyPeriod === "function") onApplyPeriod(range.start, range.end);
    if (typeof onCloseNotice === "function") onCloseNotice();
  };

  const trailDays = recentTrailDays();
  const selectedDayKeys = Array.isArray(trailDayKeys) ? trailDayKeys : [];
  const activeChip = selectedDayKeys.length
    ? ""
    : activeTrailChip(tracePeriodStart, tracePeriodEnd);
  const hour1On = selectedDayKeys.includes("1h") || activeChip === "1h";
  const hour12On = selectedDayKeys.includes("12h") || activeChip === "12h";
  const toggleExpand = (event) => {
    if (expanded) return;
    if (
      event.target.closest(
        "button, input, label, a, .dashboard-trail-people__slow-tip, .dashboard-trail-people__pick-hint"
      )
    ) {
      return;
    }
    setExpanded(true);
  };

  const catalogOperatorRows = catalogPeople(catalogOperators, operators).map((person) => ({
    id: person.id,
    name: person.name,
  }));
  const catalogMachineRows = catalogMachines(catalogEquipment, equipment).map((machine) => ({
    id: machine.ident,
    name: machine.ident,
  }));

  return (
    <div className="col-xs-12 col-md-3 col-lg-3 col-sm-6 dashboard-trail-people-slot">
      <div className="dashboard-trail-people-spacer" aria-hidden="true" />
      <div
        ref={panelRef}
        className={`x_panel no-margin-top dashboard-trail-people${
          selected ? " is-selected" : ""
        }${expanded ? " is-expanded" : ""}`}
        onClick={toggleExpand}
      >
        <div className="dashboard-trail-people__head">
          <span className="dashboard-trail-people__title-row">
            <span className="dashboard-trail-people__title">{t("Rastros na tela")}</span>
            <button
              type="button"
              className="dashboard-trail-people__info"
              aria-expanded={slowTipOpen}
              aria-label={t("Por que o mapa pode demorar")}
              title={t("Por que o mapa pode demorar")}
              onClick={() => setSlowTipOpen((open) => !open)}
            >
              <FaInfoCircle />
            </button>
          </span>
          <span className="dashboard-trail-people__actions">
            <span
              className="dashboard-trail-people__tip-wrap"
              onMouseEnter={() => openQuickTip("pick")}
              onMouseLeave={() => closeQuickTip("pick")}
            >
              <button
                type="button"
                className={`dashboard-trail-people__pick${
                  landplotPickMode ? " is-on" : ""
                }`}
                onClick={(event) => {
                  event.stopPropagation();
                  if (typeof onToggleLandplotPickMode === "function") {
                    onToggleLandplotPickMode();
                  }
                }}
                aria-label={t("Escolher talhão no mapa")}
                aria-pressed={Boolean(landplotPickMode)}
              >
                <FaMapMarkedAlt />
              </button>
              {pickTipOpen ? (
                <span className="dashboard-trail-people__quick-tip" role="tooltip">
                  {t(
                    "Escolher talhão: ligue e clique no mapa, ou dois cliques bem rápidos (até 300 ms) em qualquer ponto do talhão."
                  )}
                </span>
              ) : null}
            </span>
            <span
              className="dashboard-trail-people__tip-wrap"
              onMouseEnter={() => openQuickTip("group")}
              onMouseLeave={() => closeQuickTip("group")}
            >
              <button
                type="button"
                className="dashboard-trail-people__group"
                onClick={openGroup}
                aria-label={t("Criar ou filtrar grupos")}
              >
                <FaUsers />
              </button>
              {groupTipOpen ? (
                <span className="dashboard-trail-people__quick-tip" role="tooltip">
                  {t("Criar ou filtrar grupos")}
                </span>
              ) : null}
            </span>
          </span>
        </div>
        {landplotPickMode ? (
          <p className="dashboard-trail-people__pick-hint">
            {t("Modo ligado: clique no talhão.")}
          </p>
        ) : null}
        {slowTipOpen ? (
          <div className="dashboard-trail-people__slow-tip" role="dialog">
            <p>
              {t(
                "Quanto mais operadores e máquinas estiverem marcados, e quanto maior o período, mais a plataforma demora para abrir o mapa. Num dia ou período longo, marque só quem você precisa ver."
              )}
            </p>
          </div>
        ) : null}
        <div className="dashboard-trail-people__periods">
          <button
            type="button"
            className={hour1On ? "is-on" : ""}
            title={t("Última 1 h")}
            aria-label={t("Última 1 h")}
            aria-pressed={hour1On}
            onClick={() => applyHours(1)}
          >
            1h
          </button>
          <button
            type="button"
            className={`is-wide${hour12On ? " is-on" : ""}`}
            title={t("Últimas 12 h")}
            aria-label={t("Últimas 12 h")}
            aria-pressed={hour12On}
            onClick={() => applyHours(12)}
          >
            12h
          </button>
          {trailDays.map((day) => {
            const key = trailDayKey(day);
            const label = String(day.getDate());
            const title = day.toLocaleDateString("pt-BR", {
              day: "2-digit",
              month: "2-digit",
            });
            const selectedDay = selectedDayKeys.includes(key);
            return (
              <button
                key={key}
                type="button"
                className={selectedDay ? "is-on" : ""}
                title={title}
                aria-label={title}
                aria-pressed={selectedDay}
                onClick={() => {
                  if (typeof onToggleDay === "function") onToggleDay(day);
                }}
              >
                {label}
              </button>
            );
          })}
          <button
            type="button"
            className={activeChip === "custom" ? "is-on" : ""}
            title={t("Personalizado")}
            aria-label={t("Personalizado")}
            onClick={onSelect}
          >
            <FaRegCalendarAlt />
          </button>
        </div>
        <p
          className="dashboard-trail-people__hint"
          title={t(
            "Marque operadores e máquinas. Use o ícone de talhão para escolher a cerca no mapa."
          )}
        >
          {t(
            "Marque operadores e máquinas. Use o ícone de talhão para escolher a cerca no mapa."
          )}
        </p>
        {people.length === 0 && machines.length === 0 ? (
          <p className="dashboard-trail-people__empty">
            {t("Nenhum rastro neste período.")}
          </p>
        ) : (
          <>
            <p className="dashboard-trail-people__count">
              {t("{{count}} com rastro", { count: shownCount })}
            </p>
            <div className="dashboard-trail-people__columns">
              <TrailColumn
                title={t("Operadores")}
                allLabel={t("Todos")}
                rows={people.map((person) => ({ id: person.id, name: person.name }))}
                checkedIds={visibleIds}
                onToggle={emitPeople}
                swatchFor={(id) =>
                  (operatorTraceColors && operatorTraceColors[id]) || "#90a4ae"
                }
              />
              <TrailColumn
                title={t("Máquinas")}
                allLabel={t("Todas")}
                rows={machines.map((machine) => ({
                  id: machine.ident,
                  name: machine.ident,
                }))}
                checkedIds={visibleMachineIdents}
                onToggle={emitMachines}
              />
            </div>
          </>
        )}
      </div>

      {groupOpen ? (
        <div className="dashboard-trail-people-modal">
          <div
            className="dashboard-trail-people-modal__backdrop"
            onClick={() => setGroupOpen(false)}
          />
          <div
            className="dashboard-trail-people-modal__card dashboard-trail-people-modal__card--group"
            role="dialog"
            aria-labelledby="trail-people-group-title"
          >
            <h3 id="trail-people-group-title">{t("Quem aparece neste quadro")}</h3>
            <p>
              {t(
                "Marque no cadastro quem entra neste quadro. Na lista de baixo você escolhe o rastro de cada um."
              )}
            </p>
            <div className="dashboard-trail-people__columns dashboard-trail-people__columns--group">
              <TrailColumn
                title={t("Operadores")}
                allLabel={t("Todos")}
                rows={catalogOperatorRows}
                checkedIds={groupOperators}
                onToggle={(id) =>
                  setGroupOperators(
                    nextVisibleTrailIds(
                      catalogOperatorRows.map((row) => String(row.id)),
                      groupOperators,
                      id
                    )
                  )
                }
                swatchFor={() => "#90a4ae"}
              />
              <TrailColumn
                title={t("Máquinas")}
                allLabel={t("Todas")}
                rows={catalogMachineRows}
                checkedIds={groupMachines}
                onToggle={(id) =>
                  setGroupMachines(
                    nextVisibleTrailIds(
                      catalogMachineRows.map((row) => String(row.id)),
                      groupMachines,
                      id
                    )
                  )
                }
              />
            </div>
            <div className="dashboard-trail-people-modal__actions">
              <button type="button" onClick={() => setGroupOpen(false)}>
                {t("Cancelar")}
              </button>
              <button type="button" onClick={saveGroup}>
                {t("Salvar")}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {noticeOpen ? (
        <div className="dashboard-trail-people-modal">
          <div
            className="dashboard-trail-people-modal__backdrop"
            onClick={onCloseNotice}
          />
          <div
            className="dashboard-trail-people-modal__card"
            role="dialog"
            aria-labelledby="trail-people-period-title"
          >
            <h3 id="trail-people-period-title">{t("Período do rastro")}</h3>
            <p>
              {t(
                "Quanto mais pessoas você marcar ao mesmo tempo, e quanto maior o período, mais tempo a plataforma leva para abrir esses rastros."
              )}
            </p>
            <p>
              {t(
                "O ícone de quem não foi marcado continua no mapa, na última posição ou em movimento."
              )}
            </p>
            <label className="dashboard-trail-people-modal__field">
              <span>{t("Início")}</span>
              <input
                type="date"
                value={startValue}
                onChange={(event) => setStartValue(event.target.value)}
              />
            </label>
            <label className="dashboard-trail-people-modal__field">
              <span>{t("Fim")}</span>
              <input
                type="date"
                value={endValue}
                onChange={(event) => setEndValue(event.target.value)}
              />
            </label>
            {periodError ? (
              <p className="dashboard-trail-people-modal__error" role="alert">
                {t(periodError)}
              </p>
            ) : (
              <p className="dashboard-trail-people-modal__note">
                {t("O período máximo é de 31 dias.")}
              </p>
            )}
            <div className="dashboard-trail-people-modal__actions">
              <button type="button" onClick={onCloseNotice}>
                {t("Cancelar")}
              </button>
              <button type="button" onClick={confirmPeriod}>
                {t("Ver rastros")}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default withTranslation()(DashboardTrailPeopleKpi);
